import { useContext, useEffect, useRef, useState } from "react";
import QRCode from "qrcode.react";
import { useStyle } from "./Style";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { NDIProofRequestApi } from "../../../services/AuthServices";
import { DispatchContext } from "../../../store";
import { SNACKBAR_OPEN } from "../../../reducers";
import ndiLogo from "../../../assets/Images/ndilogo.png";
import scanButton from "../../../assets/Images/scanIcon.png";
import playStore from "../../../assets/Images/Googleplay.webp";
import appStore from "../../../assets/Images/Apple.webp";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Helmet } from "react-helmet";
import config from "../../../utils/Config";

export default function PasswordLessLogin(props) {
  const classes = useStyle();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [deeplinkValue, setDeeplinkValue] = useState("");
  const [value, setValue] = useState("");
  const [wsError, setWsError] = useState(false);
  const dispatch = useContext(DispatchContext);
  const dataFetchedRef = useRef(false);
  const socketRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "IS_NOT_LOADING", payload: false });
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    // fetchProofRequest already surfaces the error via snackbar and rethrows;
    // catch here only prevents an unhandled promise rejection.
    fetchProofRequest().catch(() => {});
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  const fetchProofRequest = async () => {
    setWsError(false);
    dispatch({ type: "IS_NOT_LOADING", payload: false });
    try {
      const response = await NDIProofRequestApi("post", {
        proofName: "verifyUser",
        proofAttributes: [
          {
            name: process.env["REACT_APP_NDI_NAME_KEY"],
            restrictions: [
              { schema_name: process.env["REACT_APP_NDI_SCHEMA_NAME"] },
            ],
          },
          {
            name: process.env["REACT_APP_NDI_ID_KEY"],
            restrictions: [
              { schema_name: process.env["REACT_APP_NDI_SCHEMA_NAME"] },
            ],
          },
        ],
        purpose: "login",
      });
      streamNdi(response?.data?.data?.proofRequestThreadId);
      setDeeplinkValue(response.data.data.deepLinkURL);
      setValue(response.data.data.proofRequestURL);
      return response;
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: {
          isNotify: true,
          severity: "error",
          message: error.response?.data?.error || "Failed to generate QR code.",
        },
      });
      throw error;
    }
  };

  const streamNdi = (threadId) => {
    dispatch({ type: "IS_NOT_LOADING", payload: false });

    if (socketRef.current) {
      socketRef.current.close();
    }

    const wsUrl = `${config.WS_URL}/ndi/stream/${threadId}/`;
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onmessage = (event) => {
      ws.close();
      socketRef.current = null;
      try {
        const payload = JSON.parse(event.data);
        if (payload.error) {
          dispatch({
            type: SNACKBAR_OPEN,
            payload: {
              isNotify: true,
              severity: "error",
              message: payload.error,
            },
          });
          fetchProofRequest().catch(() => {});
          return;
        }
        const tokenData = { access_token: payload.access, refresh_token: payload.refresh };
        const userData = { ...payload, active: true };
        localStorage.setItem("token", JSON.stringify(tokenData));
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("role", JSON.stringify(userData));
        const path = localStorage.getItem("historyPath") || "/book-your-ticket";
        localStorage.removeItem("historyPath");
        window.location.href = path;
      } catch {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {
            isNotify: true,
            severity: "error",
            message: "Unexpected response from server.",
          },
        });
        fetchProofRequest().catch(() => {});
      }
    };

    ws.onerror = () => {
      ws.close();
      socketRef.current = null;
      setWsError(true);
    };
  };

  return (
    <div className={classes.pageWrapper}>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <div className={classes.card}>
        {/* Brand header */}
        <div className={classes.brandRow}>
          <Typography className={classes.heading}>
            Scan with <span style={{ color: "#5AC994" }}>Bhutan NDI</span>{" "}
            Wallet
          </Typography>
        </div>
        <Typography className={classes.subHeading}>
          {wsError
            ? "Connection failed."
            : value
            ? "Point your NDI app camera at the QR code"
            : "Generating QR code…"}
        </Typography>
        {wsError && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Button variant="outlined" size="small" onClick={() => fetchProofRequest().catch(() => {})}>
              Retry
            </Button>
          </Box>
        )}

        {/* QR Code */}
        <div className={classes.qrWrapper}>
          <div className={classes.qrBox}>
            {value ? (
              <QRCode
                title="Scan"
                value={value}
                imageSettings={{
                  src: ndiLogo,
                  excavate: true,
                  height: 44,
                  width: 44,
                }}
                bgColor="#FFF"
                fgColor="#000"
                size={isMobile ? 200 : 220}
              />
            ) : (
              <div
                className={classes.qrPlaceholder}
                style={{
                  width: isMobile ? 200 : 220,
                  height: isMobile ? 200 : 220,
                }}
              >
                <CircularProgress size={36} sx={{ color: "#5AC994" }} />
              </div>
            )}
          </div>
        </div>

        {/* Steps */}
        <ol
          style={{
            paddingLeft: 20,
            margin: "0 0 20px",
            listStyleType: "decimal",
          }}
        >
          <li
            style={{
              color: "#9d9d9d",
              fontSize: 13,
              marginBottom: 10,
              paddingLeft: 4,
            }}
          >
            Open Bhutan NDI on your phone
          </li>
          <li style={{ color: "#9d9d9d", fontSize: 13, paddingLeft: 4 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <p style={{ margin: 0 }}>
                Tab the Scan button located on the menu bar{" "}
              </p>
              <img
                src={scanButton}
                style={{
                  height: 22,
                  width: 22,
                  verticalAlign: "middle",
                  marginLeft: 4,
                }}
                alt=""
              />
            </div>
            <p style={{ margin: "4px 0 0" }}>and capture code</p>
          </li>
        </ol>

        {/* Mobile deeplink */}
        {isMobile && (
          <>
            <div className={classes.dividerRow}>
              <div className={classes.dividerLine} />
              <Typography className={classes.dividerLabel}>OR</Typography>
              <div className={classes.dividerLine} />
            </div>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: 14,
                color: "#9d9d9d",
                mb: 2,
              }}
            >
              Open{" "}
              <span style={{ color: "#5AC994", fontWeight: 700 }}>
                Bhutan NDI
              </span>{" "}
              Wallet{" "}
              <a
                href={deeplinkValue}
                target="_blank"
                rel="noreferrer"
                style={{ color: "#9d9d9d", textDecoration: "underline" }}
              >
                here
              </a>
            </Typography>
          </>
        )}

        {/* Video guide */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            variant="contained"
            target="_blank"
            href="https://www.youtube.com/@BhutanNDI"
            className={classes.videoBtn}
            endIcon={<PlayCircleOutlineIcon />}
          >
            Watch video guide
          </Button>
        </Box>

        {/* App store */}
        <Typography className={classes.downloadLabel}>
          DOWNLOAD THE APP
        </Typography>
        <div className={classes.storeRow}>
          <a
            href="https://play.google.com/store/search?q=bhutan%20ndi&c=apps&hl=en_IN&gl=US"
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={playStore}
              alt="Google Play"
              className={classes.storeImg}
            />
          </a>
          <a
            href="https://apps.apple.com/in/app/bhutan-ndi/id1645493166"
            target="_blank"
            rel="noreferrer"
          >
            <img src={appStore} alt="App Store" className={classes.storeImg} />
          </a>
        </div>
      </div>
    </div>
  );
}
